import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, resolveApiAssetUrl } from '../lib/api'
import { defaultCardV9TimelineYears, normalizeCardTimelineItems } from '../lib/cardTimeline'
import { publicCardOgShareUrl } from '../lib/publicShareUrls'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { initKakao } from '../lib/kakao'
import { getCardTemplateMeta, splitTags } from '../features/publicPage/registry'
import NotFoundPage from './NotFound'

function nameInitials(name) {
  const s = String(name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return s.slice(0, 2).toUpperCase()
}

function IconButton({
  label,
  href,
  onClick,
  disabled,
  labelClassName = 'text-slate-600',
  rowHoverClass,
  children,
}) {
  const cls = disabled
    ? 'opacity-40 cursor-not-allowed'
    : (rowHoverClass || 'hover:bg-slate-50')
  if (href && !disabled) {
    return (
      <a
        className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-md ${cls}`}
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noreferrer' : undefined}
      >
        {children}
        <span className={`text-[9px] leading-tight ${labelClassName}`}>{label}</span>
      </a>
    )
  }
  return (
    <button
      type="button"
      className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-md ${cls}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children}
      <span className={`text-[9px] leading-tight ${labelClassName}`}>{label}</span>
    </button>
  )
}

function Svg({ children, className = 'h-4 w-4' }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      {children}
    </svg>
  )
}

function BrandIcon({ kind }) {
  if (kind === 'youtube') {
    return (
      <svg viewBox="0 0 28 20" aria-hidden="true" className="h-3.5 w-[1.15rem]">
        <rect x="0" y="0" width="28" height="20" rx="5" fill="#FF0000" />
        <path d="M12 6.2L19 10L12 13.8V6.2Z" fill="#fff" />
      </svg>
    )
  }
  if (kind === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
        <defs>
          <linearGradient id="ig" x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FEDA75" />
            <stop offset="0.35" stopColor="#FA7E1E" />
            <stop offset="0.7" stopColor="#D62976" />
            <stop offset="1" stopColor="#4F5BD5" />
          </linearGradient>
        </defs>
        <rect x="3" y="3" width="18" height="18" rx="5" fill="url(#ig)" />
        <rect x="7" y="7" width="10" height="10" rx="3" stroke="#fff" strokeWidth="1.8" fill="none" />
        <circle cx="12" cy="12" r="2.4" stroke="#fff" strokeWidth="1.8" fill="none" />
        <circle cx="16.8" cy="7.2" r="1.1" fill="#fff" />
      </svg>
    )
  }
  if (kind === 'blog') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
        <rect x="3" y="3" width="18" height="18" rx="5" fill="#03C75A" />
        <path
          d="M8 8.2h4.4c2.3 0 3.9 1.2 3.9 3.2 0 2.1-1.6 3.3-3.9 3.3H10.7V18H8V8.2Zm2.7 2.3v2.1h1.6c.9 0 1.5-.4 1.5-1.1 0-.7-.6-1-1.5-1h-1.6Z"
          fill="#fff"
        />
      </svg>
    )
  }
  return null
}

function ContactRows({ phone, email, mobileFaxNumber, addressText, tone = 'light' }) {
  if (!(phone || email || mobileFaxNumber || addressText)) return null
  const isDark = tone === 'dark'
  const wrap = isDark ? 'space-y-4 text-sm text-slate-100' : 'space-y-4 text-sm text-slate-700'
  const lab = isDark ? 'text-[11px] font-medium tracking-wide text-white/45' : 'text-[11px] font-medium tracking-wide text-slate-400'
  return (
    <div className={wrap}>
      {phone && (
        <div>
          <div className={lab}>전화</div>
          <div className="mt-1 font-medium">{phone}</div>
        </div>
      )}
      {email && (
        <div>
          <div className={lab}>이메일</div>
          <div className="mt-1 break-all font-medium">{email}</div>
        </div>
      )}
      {mobileFaxNumber && (
        <div>
          <div className={lab}>모바일 팩스</div>
          <div className="mt-1 font-medium">{mobileFaxNumber}</div>
        </div>
      )}
      {addressText && (
        <div>
          <div className={lab}>주소</div>
          <div className="mt-1 whitespace-pre-line font-medium">{addressText}</div>
        </div>
      )}
    </div>
  )
}

function SocialActionRow({ youtubeUrl, instagramUrl, blogUrl, phoneDigits, smsHref, tone = 'light' }) {
  const isDark = tone === 'dark'
  const barCls = isDark
    ? 'flex items-stretch justify-between gap-0.5 rounded-xl bg-white/[0.05] px-0.5 py-1.5'
    : 'flex items-stretch justify-between gap-0.5 rounded-xl bg-slate-100/60 px-0.5 py-1.5'
  const labelCls = isDark ? 'text-slate-200' : 'text-slate-600'
  const rowHover = isDark ? 'hover:bg-white/10' : undefined
  const stroke = isDark ? 'text-white' : 'text-slate-700'
  return (
    <div className={barCls}>
      <IconButton label="YouTube" href={youtubeUrl} disabled={!youtubeUrl} labelClassName={labelCls} rowHoverClass={rowHover}>
        <BrandIcon kind="youtube" />
      </IconButton>
      <IconButton label="Instagram" href={instagramUrl} disabled={!instagramUrl} labelClassName={labelCls} rowHoverClass={rowHover}>
        <BrandIcon kind="instagram" />
      </IconButton>
      <IconButton label="Blog" href={blogUrl} disabled={!blogUrl} labelClassName={labelCls} rowHoverClass={rowHover}>
        <BrandIcon kind="blog" />
      </IconButton>
      <IconButton label="전화" href={phoneDigits ? `tel:${phoneDigits}` : ''} disabled={!phoneDigits} labelClassName={labelCls} rowHoverClass={rowHover}>
        <Svg className={`h-4 w-4 shrink-0 ${stroke}`}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.8 2.1Z" /></Svg>
      </IconButton>
      <IconButton label="문자" href={smsHref} disabled={!phoneDigits} labelClassName={labelCls} rowHoverClass={rowHover}>
        <Svg className={`h-4 w-4 shrink-0 ${stroke}`}><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" /></Svg>
      </IconButton>
    </div>
  )
}

function OwnerSharePanel({ isOwner, absoluteCardUrl, absoluteShareUrl, smsShareHref, copy, copied, tone = 'light' }) {
  if (!isOwner) return null
  const isDark = tone === 'dark'
  const wrapCls = isDark
    ? 'mt-4 border-t border-white/[0.08] pt-3 text-white'
    : 'mt-4 border-t border-slate-200/60 pt-3'
  const btnCls = isDark
    ? 'rounded-md border border-white/10 bg-transparent px-1.5 py-1.5 text-[11px] font-medium text-center leading-tight text-white hover:bg-white/8'
    : 'rounded-md border border-slate-200/70 bg-transparent px-1.5 py-1.5 text-[11px] font-medium text-center leading-tight text-slate-700 hover:bg-slate-50/80'
  const mutedCls = isDark ? 'text-slate-400' : 'text-slate-500'
  return (
    <div className={wrapCls}>
      <div className={`text-[11px] font-medium ${isDark ? 'text-white/50' : 'text-slate-500'}`}>공유</div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button
          type="button"
          className={btnCls}
          onClick={() => copy(absoluteShareUrl || absoluteCardUrl)}
        >
          링크복사
        </button>
        <a
          className={`${btnCls} ${!smsShareHref ? 'opacity-40 cursor-not-allowed' : ''}`}
          href={smsShareHref || undefined}
          onClick={(e) => { if (!smsShareHref) e.preventDefault() }}
        >
          문자공유
        </a>
        <button
          type="button"
          className={btnCls}
          onClick={async () => {
            try {
              initKakao()
              const Kakao = window.Kakao
              if (!Kakao?.Share?.sendScrap) throw new Error('kakao_share_not_supported')
              Kakao.Share.sendScrap({ requestUrl: absoluteShareUrl })
              return
            } catch {
              await copy(absoluteShareUrl)
            }
          }}
        >
          카카오톡 공유
        </button>
      </div>
      {copied && (
        <div className={`mt-1.5 text-[10px] ${mutedCls}`}>복사됨</div>
      )}
    </div>
  )
}

function LandingLinkButton({ landingEnabled, showLandingLink, landingUrlPath, variant = 'primary', cardPreviewMode = false }) {
  if (!(landingEnabled && showLandingLink)) return null
  const cls = variant === 'subtle'
    ? 'block w-full rounded-lg border border-white/12 text-center text-xs font-medium py-2 text-white hover:bg-white/8'
    : 'block w-full rounded-lg bg-brand-sky text-white text-center text-xs font-medium py-2 hover:opacity-90'
  if (cardPreviewMode) {
    const stubCls = `${cls} cursor-default opacity-95`
    return (
      <div className="mt-3">
        <span className={stubCls} role="note">소개페이지 보기</span>
      </div>
    )
  }
  return (
    <div className="mt-3">
      <Link className={cls} to={landingUrlPath}>
        소개페이지 보기
      </Link>
    </div>
  )
}

function PrimaryActions({ downloadVcard, kakaoChatUrl, dark = false }) {
  return (
    <div className="mt-1 grid grid-cols-1 gap-2">
      <button
        type="button"
        className={`rounded-lg px-3 py-2 text-center text-xs font-medium ${dark ? 'bg-white text-slate-900' : 'bg-brand-navy text-white hover:opacity-90'}`}
        onClick={downloadVcard}
      >
        연락처 저장
      </button>
      {kakaoChatUrl && (
        <a
          className={`rounded-lg px-3 py-2 text-center text-xs font-medium border ${dark ? 'border-white/15 text-white hover:bg-white/8' : 'border-slate-200 text-slate-800 hover:bg-slate-50'}`}
          href={kakaoChatUrl}
          target="_blank"
          rel="noreferrer"
        >
          카카오 상담하기
        </a>
      )}
    </div>
  )
}

function CardTemplateDefault(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    addressText,
    mobileFaxNumber,
    email,
    phone,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
      <div className="px-6 py-6 bg-gradient-to-b from-blue-600 to-blue-500 text-white">
        <div className="flex items-center gap-5">
          <div className="h-28 w-28 shrink-0 rounded-full overflow-hidden">
            {profileImageUrl ? (
              <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full object-cover object-center" decoding="async" loading="eager" />
            ) : (
              <div className="h-full w-full rounded-full bg-white/30" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-xl font-extrabold tracking-tight truncate">{name || '이름'}</div>
            <div className="text-base opacity-90 truncate">{companyName || org || ''}</div>
            {title ? <div className="mt-1 text-sm opacity-90">{title}</div> : null}
          </div>
        </div>
      </div>
      <div className="space-y-6 p-6">
        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
        />

        {intro && (
          <div className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">
            {intro}
          </div>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-slate-300" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        )}

        <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />

        {activity && (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-slate-400">활동 지역</div>
            <div className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-700">{activity}</div>
          </div>
        )}

        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function CardTemplatePremium(props) {
  const {
    name,
    title,
    companyName,
    cardOrgLogoUrl = '',
    intro,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    activity,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-800 bg-slate-950 text-white shadow-2xl">
      <div className="relative px-6 py-7">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.24),transparent_36%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.18),transparent_32%)]" />
        {cardOrgLogoUrl ? (
          <div className="absolute right-5 top-5 z-10 h-11 w-11 overflow-hidden rounded-xl bg-white/10 p-1 ring-1 ring-white/15">
            <img src={resolveApiAssetUrl(cardOrgLogoUrl)} alt="" className="h-full w-full object-contain object-center" decoding="async" loading="eager" />
          </div>
        ) : null}
        <div className="relative flex items-center gap-5">
          <div className="h-32 w-32 shrink-0 overflow-hidden rounded-3xl bg-white/10 ring-1 ring-white/10">
            {profileImageUrl ? (
              <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full object-cover object-center" decoding="async" loading="eager" />
            ) : (
              <div className="h-full w-full bg-white/10" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            {/* <div className="text-xs uppercase tracking-[0.32em] text-sky-200/80">디지털 명함</div> */}
            <div className="mt-2 truncate text-2xl font-semibold">{name || '이름'}</div>
            <div className="mt-1 truncate text-sm text-slate-300">{companyName || ''}</div>
            {title ? (
              <div className="mt-3 text-xs tracking-wide text-slate-300/90">
                {title}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="space-y-6 px-6 pb-6">
        {intro && (
          <div className="text-sm leading-relaxed text-slate-100/90 whitespace-pre-line">
            {intro}
          </div>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-white/25" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        )}

        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
          tone="dark"
        />

        {(phone || email || mobileFaxNumber || addressText || activity) ? (
          <div className="space-y-5">
            <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} tone="dark" />
            {activity ? (
              <div>
                <div className="text-[11px] font-medium tracking-wide text-white/40">활동 지역</div>
                <div className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-100/95">{activity}</div>
              </div>
            ) : null}
          </div>
        ) : null}

        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} dark />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
          tone="dark"
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          variant="subtle"
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function CardTemplateClean(props) {
  const {
    name,
    title,
    companyName,
    intro,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    activity,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  return (
    <div className="rounded-[28px] border bg-white shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-sky-50/80 to-white px-6 py-6">
        <div className="flex items-center gap-5">
          <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-slate-100/80 ring-1 ring-slate-200/60">
            {profileImageUrl ? (
              <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full object-cover object-center" decoding="async" loading="eager" />
            ) : (
              <div className="h-full w-full bg-slate-100" />
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate text-xl font-bold text-slate-900">{name || '이름'}</div>
            <div className="mt-1 truncate text-sm text-slate-500">{companyName || ''}</div>
            {title ? (
              <div className="mt-2 text-xs font-medium tracking-wide text-slate-600">
                {title}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-6 px-6 py-6">
        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
        />

        {intro && (
          <div className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">
            {intro}
          </div>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-sky-800/80">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-sky-200" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        )}

        <div className="space-y-5">
          <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />
          {activity ? (
            <div>
              <div className="text-[11px] font-medium tracking-wide text-slate-400">활동 지역</div>
              <div className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-700">{activity}</div>
            </div>
          ) : null}
        </div>

        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function DetailLineBlue({ label, children }) {
  return (
    <div className="text-center text-sm">
      {label ? <div className="text-[11px] font-medium tracking-wide text-sky-200/55">{label}</div> : null}
      <div className="mt-1.5 leading-relaxed text-white/92">{children}</div>
    </div>
  )
}

function CardTemplateCenterBlue(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const brandLine = companyName || org || ''
  // 상단에 전화를 이미 강조하므로 하단 상세에는 중복 없이 나머지만 표시
  const hasDetailBlock = Boolean(email || mobileFaxNumber || addressText || kakaoChatUrl)

  return (
    <div className="overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-[#1a4a7a] to-[#0f2d4d] text-white shadow-2xl">
      <div className="px-6 pb-6 pt-8 text-center">
        <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-white/10 text-2xl font-bold tracking-tight ring-1 ring-white/15">
          {profileImageUrl ? (
            <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full object-cover object-center" decoding="async" loading="eager" />
          ) : (
            nameInitials(name)
          )}
        </div>
        {brandLine ? (
          <div className="mt-4 text-sm font-semibold tracking-wide text-white/90">{brandLine}</div>
        ) : null}
        {intro ? (
          <p className="mx-auto mt-2 max-w-[280px] text-sm leading-relaxed text-white/75 whitespace-pre-line">
            {intro}
          </p>
        ) : null}
        {title ? <div className="mt-3 text-xs text-sky-200/90">{title}</div> : null}
        <h1 className="mt-3 text-3xl font-bold tracking-tight">{name || '이름'}</h1>
        {phoneDigits ? (
          <a
            className="mt-4 inline-flex items-center gap-2 text-lg font-semibold text-white hover:underline"
            href={`tel:${phoneDigits}`}
          >
            <Svg className="h-5 w-5 shrink-0 text-sky-200">
              <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.8 2.1Z" />
            </Svg>
            {phone}
          </a>
        ) : phone ? <div className="mt-4 text-lg font-semibold">{phone}</div> : null}
      </div>

      <div className="space-y-6 px-6 pb-6 pt-2">
        {hasDetailBlock ? (
          <div className="space-y-5">
            {email ? (
              <DetailLineBlue label="이메일">
                <a className="break-all hover:underline" href={`mailto:${email}`}>{email}</a>
              </DetailLineBlue>
            ) : null}
            {mobileFaxNumber ? (
              <DetailLineBlue label="모바일 팩스">
                <span>{mobileFaxNumber}</span>
              </DetailLineBlue>
            ) : null}
            {addressText ? (
              <DetailLineBlue label="주소">
                <span className="whitespace-pre-line">{addressText}</span>
              </DetailLineBlue>
            ) : null}
            {kakaoChatUrl ? (
              <DetailLineBlue label="카카오">
                <a className="hover:underline" href={kakaoChatUrl} target="_blank" rel="noreferrer">카카오로 상담 연결</a>
              </DetailLineBlue>
            ) : null}
          </div>
        ) : null}

        {tags.length > 0 ? (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-white/65">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-white/25" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        ) : null}

        {activity ? (
          <div className="mt-6 text-center text-sm leading-relaxed text-white/85 whitespace-pre-line">
            <div className="text-[11px] font-medium tracking-wide text-sky-200/55">활동 지역</div>
            <div className="mt-1.5">{activity}</div>
          </div>
        ) : null}

        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
          tone="dark"
        />

        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} dark />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
          tone="dark"
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          variant="subtle"
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function CardTemplateSplitBrand(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const topLine = companyName || org || ''
  const orgSubtitle = companyName && org && String(org).trim() !== String(companyName).trim()

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-xl">
      <div className="bg-white px-6 py-5 text-center">
        {topLine ? <div className="text-base font-bold text-slate-900">{topLine}</div> : null}
        {intro ? (
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600 whitespace-pre-line">
            {intro}
          </p>
        ) : null}
      </div>

      <div className="bg-brand-navy px-6 py-8 text-white">
        {orgSubtitle ? <div className="text-xs text-sky-100/80">{org}</div> : null}
        <h2 className={`text-3xl font-bold tracking-tight ${orgSubtitle ? 'mt-1' : ''}`}>{name || '이름'}</h2>
        {title ? <div className="mt-2 text-sm text-white/90">{title}</div> : null}
        {(phoneDigits || phone) ? (
          <div className="mt-6">
            <div className="text-[11px] font-medium tracking-wide text-sky-200/80">상담·연락</div>
            {phoneDigits ? (
              <a className="mt-1 block text-2xl font-bold tracking-wide hover:underline" href={`tel:${phoneDigits}`}>
                {phone}
              </a>
            ) : (
              <div className="mt-1 text-2xl font-bold tracking-wide">{phone}</div>
            )}
          </div>
        ) : null}
      </div>

      <div className="space-y-6 bg-white px-6 py-6">
        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
        />
        <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />
        {activity ? (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-slate-400">활동 지역</div>
            <div className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-700">{activity}</div>
          </div>
        ) : null}
        {tags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-slate-300" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        ) : null}
        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>

      {profileImageUrl ? (
        <div className="relative h-56 w-full bg-slate-100">
          <img
            src={resolveApiAssetUrl(profileImageUrl)}
            alt=""
            className="h-full w-full object-cover object-center"
            decoding="async"
            loading="lazy"
          />
        </div>
      ) : null}
    </div>
  )
}

function CardTemplateMonoDark(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const brand = companyName || org || ''

  return (
    <div className="overflow-hidden rounded-[28px] border border-white/[0.08] bg-neutral-950 text-white shadow-2xl">
      <div className="px-6 pt-8 text-center">
        {brand ? <div className="text-[11px] font-medium uppercase tracking-[0.35em] text-white/45">{brand}</div> : null}
        <h1 className="mt-3 text-3xl font-bold tracking-tight">{name || '이름'}</h1>
        {title ? <div className="mt-2 text-sm text-white/60">{title}</div> : null}
      </div>

      <div className="space-y-8 px-6 pb-6 pt-6 text-center">
        {intro ? (
          <p className="text-sm leading-relaxed text-white/70 whitespace-pre-line">{intro}</p>
        ) : null}

        {(phoneDigits || phone) ? (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-white/40">전화</div>
            {phoneDigits ? (
              <a className="mt-2 inline-block text-2xl font-semibold tracking-wide hover:text-sky-300" href={`tel:${phoneDigits}`}>
                {phone}
              </a>
            ) : (
              <div className="mt-2 text-2xl font-semibold tracking-wide">{phone}</div>
            )}
          </div>
        ) : null}

        {email ? (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-white/40">이메일</div>
            <a className="mt-2 block break-all text-sm text-white/85 hover:text-sky-300" href={`mailto:${email}`}>{email}</a>
          </div>
        ) : null}

        {mobileFaxNumber ? (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-white/40">모바일 팩스</div>
            <div className="mt-2 text-sm text-white/85">{mobileFaxNumber}</div>
          </div>
        ) : null}

        {addressText ? (
          <div>
            <div className="text-[11px] font-medium tracking-wide text-white/40">주소</div>
            <div className="mt-2 whitespace-pre-line text-sm text-white/75">{addressText}</div>
          </div>
        ) : null}

        {activity ? (
          <div className="text-sm text-white/70 whitespace-pre-line">
            <div className="text-[11px] font-medium tracking-wide text-white/40">활동 지역</div>
            <div className="mt-2">{activity}</div>
          </div>
        ) : null}

        {tags.length > 0 ? (
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-white/65">
            {tags.map((tag, i) => (
              <span key={tag} className="inline-flex items-center gap-2">
                {i > 0 ? <span className="text-white/25" aria-hidden>·</span> : null}
                <span>{tag}</span>
              </span>
            ))}
          </div>
        ) : null}

        {profileImageUrl ? (
          <div className="mx-auto h-36 w-36 overflow-hidden rounded-2xl ring-1 ring-white/10">
            <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full object-cover object-center" decoding="async" loading="lazy" />
          </div>
        ) : null}

        <div>
          <SocialActionRow
            youtubeUrl={youtubeUrl}
            instagramUrl={instagramUrl}
            blogUrl={blogUrl}
            phoneDigits={phoneDigits}
            smsHref={smsHref}
            tone="dark"
          />
        </div>

        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} dark />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
          tone="dark"
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          variant="subtle"
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function CardTemplateForest(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const brand = companyName || org || ''

  return (
    <div className="rounded-[28px] bg-[#2d4a3e] p-[3px] shadow-xl">
      <div className="overflow-hidden rounded-[25px] bg-[#f7f5f0]">
        <div className="px-6 pt-7 pb-5 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#2d4a3e] text-base font-bold text-white shadow-md">
            {profileImageUrl ? (
              <img src={resolveApiAssetUrl(profileImageUrl)} alt="" className="h-full w-full rounded-full object-cover object-center" decoding="async" loading="eager" />
            ) : (
              nameInitials(name)
            )}
          </div>
          {brand ? (
            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#5c7d6e]">{brand}</div>
          ) : null}
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{name || '이름'}</h1>
          {title ? <div className="mt-1.5 text-sm font-medium text-[#2d4a3e]">{title}</div> : null}
        </div>

        <div className="space-y-6 px-6 pb-6">
          {intro ? (
            <div className="text-center text-sm leading-relaxed text-slate-700 whitespace-pre-line">
              {intro}
            </div>
          ) : null}

          {(phone || email || mobileFaxNumber || addressText) ? (
            <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />
          ) : null}

          {activity ? (
            <div className="text-center text-sm text-slate-700">
              <div className="text-[11px] font-medium tracking-wide text-[#5c7d6e]">활동 지역</div>
              <div className="mt-1 whitespace-pre-line leading-relaxed">{activity}</div>
            </div>
          ) : null}

          {tags.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-[#2d4a3e]/90">
              {tags.map((tag, i) => (
                <span key={tag} className="inline-flex items-center gap-2">
                  {i > 0 ? <span className="text-[#2d4a3e]/25" aria-hidden>·</span> : null}
                  <span>{tag}</span>
                </span>
              ))}
            </div>
          ) : null}

          <SocialActionRow
            youtubeUrl={youtubeUrl}
            instagramUrl={instagramUrl}
            blogUrl={blogUrl}
            phoneDigits={phoneDigits}
            smsHref={smsHref}
          />

          <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
          <OwnerSharePanel
            isOwner={isOwner}
            absoluteCardUrl={absoluteCardUrl}
            absoluteShareUrl={absoluteShareUrl}
            smsShareHref={smsShareHref}
            copy={copy}
            copied={copied}
          />
          <LandingLinkButton
            landingEnabled={landingEnabled}
            showLandingLink={showLandingLink}
            landingUrlPath={landingUrlPath}
            cardPreviewMode={cardPreviewMode}
          />
        </div>
      </div>
    </div>
  )
}

/** CARD_V12 — 프로필사진강조: 대형 프로필(~화면 높이 대부분) + 겹침 글래스 정보 + 하단 연락처 리스트 */
function SliceContactRowsV12({ phone, phoneDigits, email, mobileFaxNumber, addressText }) {
  const rows = []
  if (phone) {
    rows.push({
      key: 'mobile',
      label: '휴대전화',
      value: phone,
      href: phoneDigits ? `tel:${phoneDigits}` : null,
    })
  }
  if (email) {
    rows.push({ key: 'email', label: '이메일', value: email, href: `mailto:${email}` })
  }
  if (mobileFaxNumber) {
    rows.push({ key: 'fax', label: '모바일 팩스', value: mobileFaxNumber, href: null })
  }
  if (addressText) {
    rows.push({ key: 'addr', label: '주소', value: addressText, href: null })
  }
  if (!rows.length) return null
  const rowCls = 'flex items-center gap-2 py-3.5'
  return (
    <div className="divide-y divide-slate-100">
      {rows.map((r) => {
        const body = (
          <>
            <div className="w-[4.75rem] shrink-0 text-[11px] font-medium tracking-wide text-slate-400">{r.label}</div>
            <div className="min-w-0 flex-1 text-right text-sm font-semibold leading-snug tracking-tight text-slate-900">{r.value}</div>
            {r.href ? (
              <span className="shrink-0 pl-0.5 text-lg leading-none text-slate-300" aria-hidden>›</span>
            ) : (
              <span className="w-5 shrink-0" aria-hidden />
            )}
          </>
        )
        if (r.href) {
          return (
            <a key={r.key} href={r.href} className={`${rowCls} hover:bg-slate-50/90`}>
              {body}
            </a>
          )
        }
        return (
          <div key={r.key} className={rowCls}>
            {body}
          </div>
        )
      })}
    </div>
  )
}

function CardTemplateSliceHero(props) {
  const {
    name,
    title,
    companyName,
    org,
    profileImageUrl,
    cardOrgLogoUrl = '',
    tags,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const brandLines = [title, companyName || org].filter(Boolean)

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-xl">
      <div className="relative w-full min-h-[62vh] max-h-[80vh] sm:min-h-[28rem] sm:max-h-[40rem]">
        {profileImageUrl ? (
          <img
            src={resolveApiAssetUrl(profileImageUrl)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
            decoding="async"
            loading="eager"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-200 via-slate-300 to-slate-400 text-6xl font-bold text-white/50">
            {nameInitials(name)}
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/[0.06] via-transparent to-black/[0.12]" />

        <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4">
          <div className="rounded-2xl border border-white/55 bg-white/70 px-4 py-3 shadow-[0_8px_32px_rgba(15,23,42,0.12)] backdrop-blur-md supports-[backdrop-filter]:bg-white/55">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[1.35rem] font-extrabold leading-tight tracking-tight text-slate-900">
                  {name || '이름'}
                </div>
              </div>
              {cardOrgLogoUrl ? (
                <div className="h-9 max-w-[7.5rem] shrink-0 overflow-hidden rounded-lg bg-white/90 px-1.5 py-1 ring-1 ring-slate-200/80">
                  <img
                    src={resolveApiAssetUrl(cardOrgLogoUrl)}
                    alt=""
                    className="h-full w-full object-contain object-center"
                    decoding="async"
                    loading="eager"
                  />
                </div>
              ) : null}
            </div>
            {brandLines.length > 0 ? (
              <div className="mt-1.5 space-y-0.5 text-[11px] font-semibold leading-snug text-slate-600">
                {brandLines.map((line) => (
                  <div key={line} className="whitespace-pre-line">{line}</div>
                ))}
              </div>
            ) : null}
            {tags.length > 0 ? (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-slate-200/95 px-2 py-0.5 text-[10px] font-bold tracking-tight text-slate-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 bg-white px-4 pb-5 pt-1 sm:px-5">
        <SliceContactRowsV12 phone={phone} phoneDigits={phoneDigits} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />
        <div className="mt-4">
          <SocialActionRow
            youtubeUrl={youtubeUrl}
            instagramUrl={instagramUrl}
            blogUrl={blogUrl}
            phoneDigits={phoneDigits}
            smsHref={smsHref}
          />
        </div>
        <div className="mt-4">
          <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
        </div>
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

function CardTemplateMagazine(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
  } = props

  const brand = companyName || org || ''

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-xl">
      <div className="relative h-64 w-full bg-slate-800">
        {profileImageUrl ? (
          <img
            src={resolveApiAssetUrl(profileImageUrl)}
            alt=""
            className="h-full w-full object-cover object-center"
            decoding="async"
            loading="eager"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-5xl font-bold text-white/25">
            {nameInitials(name)}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          {brand ? <div className="text-xs font-medium text-white/80">{brand}</div> : null}
          <div className="mt-1 text-2xl font-bold leading-tight">{name || '이름'}</div>
          {title ? <div className="mt-1 text-sm text-white/85">{title}</div> : null}
        </div>
      </div>

      <div className="relative -mt-5 rounded-t-[28px] bg-white px-6 pb-6 pt-7 shadow-[0_-4px_24px_rgba(15,23,42,0.04)]">
        <div className="space-y-6">
          {(phoneDigits || phone) ? (
            <div className="text-center">
              <div className="text-[11px] font-medium tracking-wide text-slate-400">연락처</div>
              {phoneDigits ? (
                <a className="mt-1 inline-block text-xl font-bold text-slate-900 hover:text-brand-sky" href={`tel:${phoneDigits}`}>
                  {phone}
                </a>
              ) : (
                <div className="mt-1 text-xl font-bold text-slate-900">{phone}</div>
              )}
            </div>
          ) : null}

          {intro ? (
            <p className="text-center text-sm leading-relaxed text-slate-600 whitespace-pre-line">
              {intro}
            </p>
          ) : null}

          <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} />

          {activity ? (
            <div className="text-center text-sm text-slate-700">
              <div className="text-[11px] font-medium tracking-wide text-slate-400">활동 지역</div>
              <div className="mt-1 whitespace-pre-line leading-relaxed">{activity}</div>
            </div>
          ) : null}

          {tags.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-slate-500">
              {tags.map((tag, i) => (
                <span key={tag} className="inline-flex items-center gap-2">
                  {i > 0 ? <span className="text-slate-300" aria-hidden>·</span> : null}
                  <span>{tag}</span>
                </span>
              ))}
            </div>
          ) : null}

          <SocialActionRow
            youtubeUrl={youtubeUrl}
            instagramUrl={instagramUrl}
            blogUrl={blogUrl}
            phoneDigits={phoneDigits}
            smsHref={smsHref}
          />

          <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
          <OwnerSharePanel
            isOwner={isOwner}
            absoluteCardUrl={absoluteCardUrl}
            absoluteShareUrl={absoluteShareUrl}
            smsShareHref={smsShareHref}
            copy={copy}
            copied={copied}
          />
          <LandingLinkButton
            landingEnabled={landingEnabled}
            showLandingLink={showLandingLink}
            landingUrlPath={landingUrlPath}
            cardPreviewMode={cardPreviewMode}
          />
        </div>
      </div>
    </div>
  )
}

const EXEC_CARD_STAT_RE = /(\d[\d,\s]*\+?)\s*(건|명|회)/

function parseExecutiveCardStat(intro, activity) {
  const blob = [intro, activity].filter(Boolean).join('\n')
  const m = blob.match(EXEC_CARD_STAT_RE)
  if (!m) return null
  return { num: m[1].replace(/\s/g, ''), unit: m[2] }
}

function ExecutiveNavyServiceIcon({ index, active }) {
  const cls = `h-6 w-6 shrink-0 ${active ? 'text-sky-400' : 'text-white/45'}`
  const s = { strokeWidth: 1.65, strokeLinecap: 'round', strokeLinejoin: 'round' }
  switch (index % 5) {
    case 0:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...s}>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M16.5 16.5L21 21" />
          <rect x="5" y="15" width="9" height="5" rx="1" />
        </svg>
      )
    case 1:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...s}>
          <path d="M12 3l8 4v6c0 4.5-3.2 8.7-8 10-4.8-1.3-8-5.5-8-10V7l8-4z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      )
    case 2:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...s}>
          <path d="M12 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" />
          <path d="M5 20.5v-.5a7 7 0 0114 0v.5" />
          <path d="M17 8l2-2M19 6l2 2" />
        </svg>
      )
    case 3:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...s}>
          <path d="M8 11a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM16 11a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
          <path d="M4.5 20v-1a6 6 0 0111.5-2A6 6 0 0120 19v1" />
        </svg>
      )
    default:
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...s}>
          <path d="M4 20V9l8-5 8 5v11" />
          <path d="M9 20v-6h6v6" />
        </svg>
      )
  }
}

function CardTemplateTimelineNavy(props) {
  const {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    phone,
    email,
    mobileFaxNumber,
    addressText,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
    cardTimelineItems,
  } = props

  const orgParts = String(org || '')
    .split('·')
    .map((s) => s.trim())
    .filter(Boolean)
  const brandPrimary = (companyName || orgParts[0] || '').trim()
  const orgUnit = orgParts.length > 1 ? orgParts[orgParts.length - 1] : (orgParts[0] && orgParts[0] !== brandPrimary ? orgParts[0] : '')
  const activityHead = activity ? String(activity).trim().split('\n').filter(Boolean)[0] : ''
  const sloganSource = intro ? String(intro).trim().split('\n').filter(Boolean)[0] : ''
  const slogan = sloganSource || '실력이 계약을 지킵니다.'
  const statParsed = parseExecutiveCardStat(intro, activity)
  const defaultSlots = ['맞춤 설계', '보장 점검', '상담 응대', '장기 케어', '신뢰 파트너']
  const serviceSlots = Array.from({ length: 5 }, (_, i) => tags[i] || defaultSlots[i])
  const fallbackItems = defaultCardV9TimelineYears().map((y) => ({ year: y, label: '' }))
  const timelineItems = (() => {
    if (!Array.isArray(cardTimelineItems) || cardTimelineItems.length < 2) return fallbackItems
    const items = cardTimelineItems
      .map((row) => ({
        year: Number(row?.year),
        label: String(row?.label ?? '').trim().slice(0, 40),
      }))
      .filter((row) => Number.isFinite(row.year) && row.year >= 1900 && row.year <= 2100)
    return items.length >= 2 ? items : fallbackItems
  })()

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200/90 bg-white shadow-xl">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 text-[15px] font-bold leading-snug text-[#142a45]">
            {brandPrimary || null}
          </div>
          <div className="shrink-0 text-right text-[11px] leading-snug text-slate-500">
            {orgUnit ? <div className="font-medium text-slate-600">{orgUnit}</div> : null}
            {activityHead ? <div className={orgUnit ? 'mt-0.5' : ''}>{activityHead}</div> : null}
            {!orgUnit && !activityHead && title ? <div>{title}</div> : null}
          </div>
        </div>

        <h1 className="mt-4 text-[26px] font-bold tracking-tight text-[#142a45]">{name || '이름'}</h1>
        {title ? <div className="mt-1 text-sm text-slate-500">{title}</div> : null}

        <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2.5 text-[13px] text-slate-800">
            {phone ? (
              <div className="flex gap-2">
                <span className="w-5 shrink-0 text-left font-bold text-[#0c4a6e]">M</span>
                {phoneDigits ? (
                  <a className="min-w-0 break-all font-medium hover:text-sky-700" href={`tel:${phoneDigits}`}>{phone}</a>
                ) : (
                  <span className="font-medium">{phone}</span>
                )}
              </div>
            ) : null}
            {email ? (
              <div className="flex gap-2">
                <span className="w-5 shrink-0 font-bold text-[#0c4a6e]">E</span>
                <a className="min-w-0 break-all font-medium hover:text-sky-700" href={`mailto:${email}`}>{email}</a>
              </div>
            ) : null}
            {mobileFaxNumber ? (
              <div className="flex gap-2">
                <span className="w-5 shrink-0 font-bold text-[#0c4a6e]">F</span>
                <span className="min-w-0 font-medium">{mobileFaxNumber}</span>
              </div>
            ) : null}
            {addressText ? (
              <div className="flex gap-2">
                <span className="w-5 shrink-0 font-bold text-[#0c4a6e]">A</span>
                <span className="min-w-0 whitespace-pre-line leading-snug">{addressText}</span>
              </div>
            ) : null}
          </div>
          {landingEnabled && showLandingLink ? (
            cardPreviewMode ? (
              <span className="shrink-0 cursor-default text-[11px] font-semibold text-sky-700">
                소개페이지 보기 ▶
              </span>
            ) : (
              <Link
                className="shrink-0 text-[11px] font-semibold text-sky-700 hover:text-sky-900"
                to={landingUrlPath}
              >
                소개페이지 보기 ▶
              </Link>
            )
          ) : null}
        </div>
      </div>

      <div className="border-y border-slate-100 bg-white px-5 py-6">
        <p className="text-center text-[15px] font-semibold leading-snug text-sky-700">
          “{slogan}”
        </p>
      </div>

      <div className="bg-[#142a45] px-4 pb-6 pt-5 text-white">
        <div className="flex flex-wrap justify-between gap-y-4 sm:flex-nowrap sm:justify-between">
          {serviceSlots.map((label, i) => {
            const active = i === 4
            return (
              <div key={`${label}-${i}`} className="flex w-[18%] min-w-[52px] max-w-[64px] flex-col items-center gap-1.5 text-center sm:w-auto sm:max-w-none sm:flex-1">
                <div className={`flex h-11 w-11 items-center justify-center rounded-full ${active ? 'bg-sky-400/25 ring-2 ring-sky-400/80' : 'bg-white/5'}`}>
                  <ExecutiveNavyServiceIcon index={i} active={active} />
                </div>
                <span className={`text-[9px] font-medium leading-tight ${active ? 'text-sky-300' : 'text-white/65'}`}>
                  {label}
                </span>
              </div>
            )
          })}
        </div>

        <div className="mt-7 px-0.5">
          <div className="relative flex justify-between">
            <div className="absolute left-0 right-0 top-[7px] h-[2px] bg-sky-400/35" aria-hidden />
            {timelineItems.map((row, i) => {
              const isLast = i === timelineItems.length - 1
              const y = row.year
              return (
                <div key={`${y}-${i}`} className="relative z-[1] flex flex-col items-center" style={{ width: `${100 / timelineItems.length}%` }}>
                  <div
                    className={`rounded-full bg-sky-400 ${isLast ? 'h-3.5 w-3.5 ring-4 ring-sky-400/35' : 'h-2 w-2'}`}
                  />
                  <div className={`mt-2 text-[10px] font-semibold ${isLast ? 'text-sky-300' : 'text-white/55'}`}>{y}</div>
                  {row.label ? (
                    <div className={`mt-0.5 max-w-[92%] text-center text-[8px] font-medium leading-tight ${isLast ? 'text-sky-200/90' : 'text-white/45'}`}>
                      {row.label}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-8 text-center">
          {statParsed ? (
            <div className="text-lg font-bold leading-snug sm:text-xl">
              <span className="text-sky-400">
                {statParsed.num}
                {statParsed.unit}
                {String(statParsed.num).includes('+') ? '' : '+'}
              </span>
              <span className="text-white"> 보상·상담 사례</span>
            </div>
          ) : (
            <div className="text-base font-semibold text-white/90 sm:text-lg">맞춤 보장·1:1 상담을 함께합니다</div>
          )}
        </div>
      </div>

      {profileImageUrl ? (
        <div className="flex min-h-[10rem] w-full items-center justify-center bg-slate-100 px-2 py-3">
          <img
            src={resolveApiAssetUrl(profileImageUrl)}
            alt=""
            className="max-h-[12rem] w-full object-contain object-center"
            decoding="async"
            loading="lazy"
          />
        </div>
      ) : null}

      <div className="space-y-4 bg-white px-5 py-5">
        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
        />
        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

/** CARD_V10 / CARD_V11 — 정보 허브(히어로 이미지·경력·자유소개·태그 밀도) */
function CardTemplateHub({ variant, ...props }) {
  const isDark = variant === 'dark'
  const {
    name,
    title,
    companyName,
    cardOrgLogoUrl = '',
    org,
    intro,
    activity,
    addressText,
    mobileFaxNumber,
    email,
    phone,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardPreviewMode = false,
    careerText = '',
    freeText = '',
    showCareerCard = false,
    showFreeTextCard = false,
    showActivityAreaCard = true,
    imageCardUrl = '',
    imageCardEnabled = false,
  } = props

  const introLines = intro ? String(intro).trim().split('\n').filter(Boolean) : []
  const slogan = introLines[0] || ''
  const introRest = introLines.slice(1).join('\n')
  const careerBody = String(careerText || '').trim()
  const freeBody = String(freeText || '').trim()
  const showCareerBlock = Boolean(showCareerCard && careerBody)
  const showFreeBlock = Boolean(showFreeTextCard && freeBody)
  const showActivityBlock = showActivityAreaCard !== false && Boolean(String(activity || '').trim())
  const heroOn = Boolean(imageCardEnabled && imageCardUrl)

  const cardShell = isDark
    ? 'overflow-hidden rounded-[28px] border border-emerald-500/15 bg-[#0c1220] shadow-xl text-slate-100'
    : 'overflow-hidden rounded-[28px] border border-slate-200/90 bg-white shadow-xl text-slate-900'

  const pillCls = isDark
    ? 'rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[11px] font-medium text-emerald-200/95'
    : 'rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-900'

  const companyAccent = isDark ? 'text-emerald-300/90' : 'text-sky-700'

  const blockTitle = isDark ? 'text-[11px] font-semibold uppercase tracking-wide text-white/45' : 'text-[11px] font-semibold uppercase tracking-wide text-slate-400'
  const blockBox = isDark
    ? 'rounded-xl border border-white/[0.08] bg-white/[0.04] px-3.5 py-3'
    : 'rounded-xl border border-slate-100 bg-slate-50/90 px-3.5 py-3'

  const socialTone = isDark ? 'dark' : 'light'
  const contactTone = isDark ? 'dark' : 'light'

  return (
    <div className={cardShell}>
      {heroOn ? (
        <div className="relative h-40 w-full bg-slate-800">
          <img
            src={resolveApiAssetUrl(imageCardUrl)}
            alt=""
            className="h-full w-full object-cover object-center"
            decoding="async"
            loading="eager"
          />
          <div className={`pointer-events-none absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0c1220]/95 via-transparent' : 'from-white via-transparent'}`} aria-hidden />
        </div>
      ) : null}

      <div className={`px-5 ${heroOn ? 'pt-3 pb-5' : 'pt-6 pb-5'} ${heroOn ? '-mt-10 relative' : ''}`}>
        <div className={`flex gap-4 ${heroOn ? 'items-end' : 'items-start'}`}>
          <div
            className={`h-[5.25rem] w-[5.25rem] shrink-0 overflow-hidden rounded-2xl shadow-md ring-4 ${
              isDark ? 'ring-[#0c1220] bg-slate-800' : heroOn ? 'ring-white bg-white' : 'ring-white bg-slate-100'
            }`}
          >
            {profileImageUrl ? (
              <img
                src={resolveApiAssetUrl(profileImageUrl)}
                alt=""
                className="h-full w-full object-cover object-center"
                decoding="async"
                loading="eager"
              />
            ) : (
              <div className={`flex h-full w-full items-center justify-center text-lg font-bold ${isDark ? 'text-white/35' : 'text-slate-300'}`}>
                {nameInitials(name)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 pb-0.5">
            <div className="flex min-w-0 items-center gap-2">
              {cardOrgLogoUrl ? (
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/10 p-0.5 ring-1 ring-white/10">
                  <img src={resolveApiAssetUrl(cardOrgLogoUrl)} alt="" className="max-h-full max-w-full object-contain object-center" decoding="async" loading="eager" />
                </span>
              ) : null}
              <div className={`min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-wide ${companyAccent}`}>
                {companyName || org || '\u00a0'}
              </div>
            </div>
            <h1 className={`mt-1 text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>{name || '이름'}</h1>
            {title ? (
              <div className={`mt-0.5 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{title}</div>
            ) : null}
            {org && (companyName || '') !== org ? (
              <div className={`mt-1 text-[11px] leading-snug ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{org}</div>
            ) : null}
          </div>
        </div>

        {slogan ? (
          <div
            className={`mt-5 rounded-xl px-3 py-2.5 text-center text-sm font-semibold leading-snug ${
              isDark ? 'bg-emerald-500/15 text-emerald-50' : 'bg-sky-50 text-sky-900'
            }`}
          >
            {slogan}
          </div>
        ) : null}
        {introRest ? (
          <p className={`mt-2 whitespace-pre-line text-[13px] leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{introRest}</p>
        ) : null}

        {tags.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span key={tag} className={pillCls}>
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {showActivityBlock ? (
          <div className="mt-4">
            <div className={blockTitle}>활동</div>
            <div className={`mt-1.5 whitespace-pre-line text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{activity}</div>
          </div>
        ) : null}

        {showCareerBlock ? (
          <div className="mt-4">
            <div className={blockTitle}>경력 · 자격</div>
            <div className={`mt-1.5 ${blockBox}`}>
              <p className={`whitespace-pre-line text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{careerBody}</p>
            </div>
          </div>
        ) : null}

        {showFreeBlock ? (
          <div className="mt-3">
            <div className={blockTitle}>소개</div>
            <div className={`mt-1.5 ${blockBox}`}>
              <p className={`whitespace-pre-line text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{freeBody}</p>
            </div>
          </div>
        ) : null}
      </div>

      <div className={`space-y-4 px-5 py-5 ${isDark ? 'border-t border-emerald-500/10 bg-black/25' : 'border-t border-slate-100 bg-slate-50/50'}`}>
        <ContactRows phone={phone} email={email} mobileFaxNumber={mobileFaxNumber} addressText={addressText} tone={contactTone} />
        <SocialActionRow
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
          tone={socialTone}
        />
        <PrimaryActions downloadVcard={downloadVcard} kakaoChatUrl={kakaoChatUrl} dark={isDark} />
        <OwnerSharePanel
          isOwner={isOwner}
          absoluteCardUrl={absoluteCardUrl}
          absoluteShareUrl={absoluteShareUrl}
          smsShareHref={smsShareHref}
          copy={copy}
          copied={copied}
          tone={isDark ? 'dark' : 'light'}
        />
        <LandingLinkButton
          landingEnabled={landingEnabled}
          showLandingLink={showLandingLink}
          landingUrlPath={landingUrlPath}
          cardPreviewMode={cardPreviewMode}
        />
      </div>
    </div>
  )
}

export function PublicCardTemplateBody({ cardTemplateId, templateProps }) {
  switch (cardTemplateId) {
    case 'CARD_V2':
      return <CardTemplatePremium {...templateProps} />
    case 'CARD_V3':
      return <CardTemplateClean {...templateProps} />
    case 'CARD_V4':
      return <CardTemplateCenterBlue {...templateProps} />
    case 'CARD_V5':
      return <CardTemplateSplitBrand {...templateProps} />
    case 'CARD_V6':
      return <CardTemplateMonoDark {...templateProps} />
    case 'CARD_V7':
      return <CardTemplateForest {...templateProps} />
    case 'CARD_V8':
      return <CardTemplateMagazine {...templateProps} />
    case 'CARD_V9':
      return <CardTemplateTimelineNavy {...templateProps} />
    case 'CARD_V10':
      return <CardTemplateHub variant="light" {...templateProps} />
    case 'CARD_V11':
      return <CardTemplateHub variant="dark" {...templateProps} />
    case 'CARD_V12':
      return <CardTemplateSliceHero {...templateProps} />
    default:
      return <CardTemplateDefault {...templateProps} />
  }
}

const MOCK_PUBLIC_CARD_PREVIEW_PROFILE_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1200">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#dbeafe"/>
      <stop offset="0.52" stop-color="#f8fafc"/>
      <stop offset="1" stop-color="#bfdbfe"/>
    </linearGradient>
    <linearGradient id="hair" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#111827"/>
      <stop offset="1" stop-color="#334155"/>
    </linearGradient>
    <linearGradient id="suit" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0f172a"/>
      <stop offset="1" stop-color="#1e3a8a"/>
    </linearGradient>
    <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="18" stdDeviation="18" flood-color="#0f172a" flood-opacity=".18"/>
    </filter>
  </defs>
  <rect width="900" height="1200" fill="url(#bg)"/>
  <circle cx="190" cy="180" r="120" fill="#ffffff" opacity=".55"/>
  <circle cx="760" cy="280" r="160" fill="#93c5fd" opacity=".28"/>
  <g filter="url(#softShadow)">
    <path d="M190 1150c36-202 162-320 260-320s224 118 260 320H190z" fill="url(#suit)"/>
    <path d="M360 850l90 130 90-130c-25 20-55 31-90 31s-65-11-90-31z" fill="#f8fafc"/>
    <path d="M270 455c8-153 93-258 184-258 103 0 181 90 185 244 9 16 14 38 14 61 0 44-19 80-44 84-30 110-88 186-159 186s-130-76-160-187c-25-7-43-42-43-83 0-18 3-34 10-48 4 1 8 1 13 1z" fill="#f2c9ad"/>
    <path d="M286 445c49-26 92-71 119-128 66 63 139 91 219 91-14-132-86-213-176-213-101 0-170 86-185 218 5 13 13 24 23 32z" fill="url(#hair)"/>
    <path d="M266 479c-9-120 36-265 158-300 122-35 223 51 223 223 0 37-4 68-11 95-12-89-42-154-90-198-44-40-100-55-152-39-75 24-118 92-128 219z" fill="#0f172a" opacity=".92"/>
    <ellipse cx="376" cy="512" rx="15" ry="10" fill="#1f2937"/>
    <ellipse cx="523" cy="512" rx="15" ry="10" fill="#1f2937"/>
    <path d="M416 620c22 18 48 18 70 0" fill="none" stroke="#9f5f4a" stroke-width="12" stroke-linecap="round"/>
    <path d="M451 536c-10 30-19 59-29 86 17 10 38 11 56 1" fill="none" stroke="#cf9478" stroke-width="10" stroke-linecap="round"/>
    <path d="M320 1150h260l-87-240-43 69-45-69-85 240z" fill="#f8fafc" opacity=".96"/>
    <path d="M190 1150c28-161 115-270 216-302l44 131-92 171H190z" fill="#1e3a8a"/>
    <path d="M710 1150c-28-161-115-270-216-302l-44 131 92 171h168z" fill="#172554"/>
  </g>
</svg>
`)}`

/** 모바일 명함 편집 화면 샘플 미리보기용 (가상 인물) */
export const MOCK_PUBLIC_CARD_PREVIEW_PROPS = {
  name: '김서연',
  title: '수석 설계사 | 팀장',
  companyName: '예시금융서비스',
  org: '예시금융서비스 · 강남지점',
  intro: '실력이 계약을 지킵니다.\n3,200건 이상의 상담으로 맞춤 보장을 도와드립니다.',
  activity: '강남센터\n18년+ 보험·재무 설계 경력',
  addressText: '서울특별시 강남구 테헤란로 123, 10층',
  mobileFaxNumber: '0504-123-4567',
  email: 'sample.planner@example.com',
  phone: '010-1234-5678',
  phoneDigits: '01012345678',
  smsHref: 'sms:01012345678',
  youtubeUrl: 'https://www.youtube.com',
  instagramUrl: 'https://www.instagram.com',
  blogUrl: 'https://blog.example.com',
  kakaoChatUrl: 'https://open.kakao.com/o/sample',
  profileImageUrl: MOCK_PUBLIC_CARD_PREVIEW_PROFILE_IMAGE,
  tags: splitTags('#보장점검#실손정리#노후준비#상속설계#기업복리'),
  cardTimelineItems: defaultCardV9TimelineYears().map((y, i) => ({
    year: y,
    label: i === 1 ? '입사' : i === 4 ? '현재' : '',
  })),
  careerText: '생명·손해보험 설계사 등록\n협회·소속사 정기 연수 이수',
  freeText: '가입 후에도 해지·갱신·청구까지 같이 봅니다.\n증권만 가지고 오셔도 방향 제안 가능해요.',
  showCareerCard: true,
  showFreeTextCard: true,
  showActivityAreaCard: true,
  imageCardEnabled: false,
  imageCardUrl: '',
  cardOrgLogoUrl: '',
  downloadVcard: () => {},
  isOwner: false,
  absoluteCardUrl: 'https://example.com/@sample',
  absoluteShareUrl: 'https://example.com/v1/share/card/sample',
  smsShareHref: 'sms:?body=sample',
  copy: async () => {},
  copied: '',
  landingEnabled: true,
  showLandingLink: true,
  landingUrlPath: '/p/sample-preview',
  cardPreviewMode: true,
}

export function PublicCardTemplatePreview({ templateId }) {
  const id = getCardTemplateMeta(templateId).id
  return <PublicCardTemplateBody cardTemplateId={id} templateProps={MOCK_PUBLIC_CARD_PREVIEW_PROPS} />
}

export default function PublicCardPage() {
  const { code, handle } = useParams()
  const raw = (code !== undefined ? code : (handle !== undefined ? handle : '')) || ''
  const isAtHandleRoute = (handle !== undefined && code === undefined)
  const effectiveCode = raw.startsWith('@') ? raw.slice(1) : raw
  // "/:handle" 라우트는 @로 시작하는 것만 명함으로 처리 (그 외는 기존 404 화면 유지)
  const shouldNotFound = Boolean(isAtHandleRoute && raw && !raw.startsWith('@'))
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [privateOwnerView, setPrivateOwnerView] = React.useState(false)
  const [copied, setCopied] = React.useState('')

  React.useEffect(() => {
    if (shouldNotFound) {
      setLoading(false)
      setError(null)
      setData(null)
      return
    }
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      setPrivateOwnerView(false)
      try {
        const res = await api.get(`/public/card/${encodeURIComponent(effectiveCode || '')}`, { __silent: true })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setData(payload || null)
      } catch (e) {
        const status = e?.response?.status
        if (status === 404) {
          // cardEnabled=false 인 경우에도 404를 주기 때문에,
          // "로그인한 소유자"라면 비공개 안내 화면을 보여준다.
          try {
            const meRes = await api.get('/users/me/public-page', { __silent: true })
            const mePayload = (meRes?.data && meRes.data.data !== undefined) ? meRes.data.data : meRes.data
            const myCode = mePayload?.code ? String(mePayload.code) : ''
            const disabled = mePayload?.cardEnabled === false
            if (myCode && String(myCode) === String(effectiveCode) && disabled) {
              if (!ignore) {
                setData(null)
                setError(null)
                setPrivateOwnerView(true)
              }
              return
            }
          } catch { }
          if (!ignore) setError('페이지를 찾을 수 없습니다.')
        } else {
          const msg = e?.response?.data?.message || e?.message || '불러오기 실패'
          if (!ignore) setError(msg)
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [effectiveCode, shouldNotFound])

  if (shouldNotFound) return <NotFoundPage />

  if (loading) return <Loading />
  if (privateOwnerView) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
        <main className="flex-1">
          <div className="mx-auto max-w-md px-4 py-10">
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <div className="text-lg font-extrabold">비공개 처리된 명함입니다.</div>
              <div className="mt-2 text-sm text-slate-600">
                현재 모바일 명함 공개 설정이 꺼져 있어 외부에서는 열리지 않습니다.
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Link className="rounded bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:opacity-90" to="/sales/public-card">
                  모바일명함 관리로 이동
                </Link>
                <Link className="rounded border px-4 py-2 text-sm hover:bg-slate-50" to="/">
                  홈으로
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }
  if (error) return <InlineError message={error} />
  if (!data) return <InlineError message="페이지를 찾을 수 없습니다." />

  const name = data?.name || ''
  const title = String(data?.title || '').trim()
  const companyName = data?.companyName || [data?.orgRootName, data?.orgName].filter(Boolean)[0] || ''
  const org = [data?.orgRootName, data?.orgParentName, data?.orgName].filter(Boolean).join(' · ')
  const phone = data?.phoneNumber || ''
  const email = data?.email || ''
  const addressText = data?.addressText || ''
  const mobileFaxNumber = data?.mobileFaxNumber || ''
  const kakaoChatUrl = data?.kakaoChatUrl || ''
  const intro = data?.introText || data?.bioShort || ''
  const landingEnabled = Boolean(data?.landingEnabled)
  const showLandingLink = data?.showLandingLink !== false
  const profileImageUrl = data?.profileImageUrl || ''
  const youtubeUrl = data?.youtubeUrl || ''
  const instagramUrl = data?.instagramUrl || ''
  const blogUrl = data?.blogUrl || ''
  const tags = splitTags(data?.serviceTags)
  const cardTemplateId = getCardTemplateMeta(data?.cardTemplateId || 'CARD_V1').id

  const normalizedTimeline = normalizeCardTimelineItems(data)
  const cardTimelineItemsProp =
    normalizedTimeline && normalizedTimeline.length >= 2
      ? normalizedTimeline
      : defaultCardV9TimelineYears().map((y) => ({ year: y, label: '' }))

  const cardUrlPath = `/@${data?.code}`
  const landingUrlPath = `/p/${data?.code}`
  const vcardPath = `/public/vcard/${data?.code}` // axios baseURL에 의해 /v1/public/vcard 로 호출됨
  const shareUrlPath = `/v1/share/card/${data?.code}` // 상대 경로(표시·문자 본문용)
  const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : ''
  const absoluteCardUrl = origin ? `${origin}${cardUrlPath}` : cardUrlPath
  const absoluteShareUrl = publicCardOgShareUrl(data?.code, data?.ogVersion || data?.updatedAt || '') || shareUrlPath
  const phoneDigits = phone ? phone.replace(/[^0-9+]/g, '') : ''
  const smsHref = phoneDigits ? `sms:${phoneDigits}` : ''
  const isIOS = (typeof navigator !== 'undefined') ? /iPad|iPhone|iPod/.test(navigator.userAgent || '') : false
  const smsBodySep = isIOS ? '&' : '?'
  // 문자공유: 수신자 없이 본문만 채우기(공유용)
  const smsShareHref = `sms:${smsBodySep}body=${encodeURIComponent(`${name} 명함: ${absoluteShareUrl}`)}`

  // 내 명함이면 "편집하러 가기" 노출
  let isOwner = false
  try {
    const me = JSON.parse(localStorage.getItem('auth.user') || 'null')
    isOwner = Boolean(me?.id && data?.userId && String(me.id) === String(data.userId))
  } catch { }

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text) } catch { }
    setCopied(text)
    setTimeout(() => setCopied(''), 1200)
  }

  const downloadVcard = async () => {
    try {
      const res = await api.get(vcardPath, { responseType: 'blob', __silent: true })
      const contentType = res?.headers?.['content-type'] || 'text/vcard'
      const blob = new Blob([res.data], { type: contentType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(name || 'contact').trim() || 'contact'}.vcf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => {
        try { URL.revokeObjectURL(url) } catch { }
      }, 1500)
    } catch {
      // 폴백: 새 탭(서버/프록시 환경에 따라 직접 링크가 더 잘 동작할 수 있음)
      try {
        const base = api?.defaults?.baseURL || '/v1'
        const abs = base.startsWith('http')
          ? `${base.replace(/\/$/, '')}/public/vcard/${encodeURIComponent(data?.code || '')}`
          : `${origin}${base.startsWith('/') ? '' : '/'}${base.replace(/\/$/, '')}/public/vcard/${encodeURIComponent(data?.code || '')}`
        window.open(abs, '_blank', 'noopener,noreferrer')
      } catch { }
    }
  }

  const careerText = data?.careerText || ''
  const freeText = data?.freeText || ''
  const showCareerCard = data?.showCareerCard !== false
  const showFreeTextCard = data?.showFreeTextCard !== false
  const showActivityAreaCard = data?.showActivityAreaCard !== false
  const activity = showActivityAreaCard ? (data?.activityAreaText || '') : ''
  const imageCardEnabled = Boolean(data?.imageCardEnabled)
  const imageCardUrl = data?.imageCardUrl || ''
  const cardOrgLogoUrl = data?.cardOrgLogoUrl || ''

  const templateProps = {
    name,
    title,
    companyName,
    org,
    intro,
    activity,
    addressText,
    mobileFaxNumber,
    email,
    phone,
    phoneDigits,
    smsHref,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    tags,
    downloadVcard,
    isOwner,
    absoluteCardUrl,
    absoluteShareUrl,
    smsShareHref,
    copy,
    copied,
    landingEnabled,
    showLandingLink,
    landingUrlPath,
    cardTimelineItems: cardTimelineItemsProp,
    careerText,
    freeText,
    showCareerCard,
    showFreeTextCard,
    showActivityAreaCard,
    imageCardEnabled,
    imageCardUrl,
    cardOrgLogoUrl,
  }

  const immersiveDark = cardTemplateId === 'CARD_V4' || cardTemplateId === 'CARD_V6' || cardTemplateId === 'CARD_V11'
  const pageBg = cardTemplateId === 'CARD_V6'
    ? 'bg-neutral-950'
    : cardTemplateId === 'CARD_V4'
      ? 'bg-[#0c2138]'
      : cardTemplateId === 'CARD_V11'
        ? 'bg-[#030712]'
        : 'bg-slate-50'
  const pageText = immersiveDark ? 'text-slate-100' : 'text-slate-900'

  return (
    <div className={`min-h-screen flex flex-col ${pageBg} ${pageText}`}>
      <main className="flex-1">
        <div className="mx-auto max-w-md px-4 py-10 motion-safe:animate-public-card-enter motion-reduce:animate-none">
          <PublicCardTemplateBody cardTemplateId={cardTemplateId} templateProps={templateProps} />
        </div>
      </main>
    </div>
  )
}

